import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import Line from "../../components/InputComponents/Line";
import axiosClient from "../../api/api";
import {
  successToast,
  errorToast,
} from "../../adminPanel/components/toastAlerts/ToastAlerts";
import DeliveryAddressPicker from "./DeliveryAddressPicker";
import { BsX, BsExclamationTriangle } from "react-icons/bs";
import { apiError } from "../../api/apiError";

/**
 * Change the delivery address of a parcel that already has one.
 *
 * Moving a parcel between zones changes its price, so this can't be a silent
 * save: the difference is shown before confirming, and settled afterwards —
 * refunded to the balance, taken from the balance, or left owed with a link to
 * top up. What the backend decided is reported back rather than assumed here.
 */
const ChangeDeliveryAddress = ({ setDecShow, data, setUpdatePacks }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const currentId = data?.user_address_id ? String(data.user_address_id) : "";
  const [addressId, setAddressId] = useState(currentId);
  const [saving, setSaving] = useState(false);
  const [quote, setQuote] = useState(null); // price at the newly picked address
  const [result, setResult] = useState(null); // what the settlement did

  const currentPrice = Number(data?.transport_price || 0);
  const changed = addressId && addressId !== currentId;

  // Preview the new price so the customer sees the difference before agreeing.
  useEffect(() => {
    if (!changed) {
      setQuote(null);
      return;
    }
    let alive = true;
    axiosClient
      .get(`/packs/${data.id}/quote`, { params: { user_address_id: addressId } })
      .then(({ data: q }) => alive && setQuote(q))
      .catch(() => alive && setQuote(null));
    return () => {
      alive = false;
    };
  }, [addressId, changed, data.id]);

  const delta = quote ? Number(quote.price) - currentPrice : 0;

  const save = async () => {
    if (!addressId) {
      errorToast(t("choose_address") || "აირჩიეთ მიტანის მისამართი");
      return;
    }
    setSaving(true);
    try {
      const { data: res } = await axiosClient.post(
        `/packs/${data.id}/delivery-address`,
        { user_address_id: addressId }
      );
      setResult(res);
      setUpdatePacks(res);

      if (res.outcome === "refunded") {
        successToast(`${Number(res.amount).toFixed(2)} ₾ დაგიბრუნდათ ბალანსზე`);
      } else if (res.outcome === "charged_balance") {
        successToast(`${Number(res.amount).toFixed(2)} ₾ ჩამოგეჭრათ ბალანსიდან`);
      } else if (res.outcome === "needs_payment") {
        errorToast(`დარჩა გადასახდელი ${Number(res.amount).toFixed(2)} ₾`);
      } else {
        successToast(t("successfully") || "შენახულია");
      }
    } catch (e) {
      errorToast(apiError(e, t("something_went_wrong") || "error"));
    }
    setSaving(false);
  };

  return (
    <div
      className="w-screen h-screen bg-white bg-opacity-80 flex items-center justify-center
        fixed top-0 left-0 z-50"
    >
      <div
        className="bg-white flex flex-col w-[50%] md:w-[90%] p-7 sm:px-5
            border border-gray-300 shadow-sm rounded-[30px] relative"
      >
        <BsX
          onClick={() => setDecShow(false)}
          className="absolute top-5 right-5 text-gray-400 w-6 h-6 cursor-pointer"
        />
        <div className="flex flex-col gap-2 items-center justify-center w-full">
          <Line className="bg-teal-600 h-0.5 w-20" />
          <h2 className="text-center font-semibold w-auto text-xl">
            {t("delivery_address") || "მიტანის მისამართი"}
          </h2>
        </div>
        <Line className="bg-gray_300 h-0.5 w-full my-5" />

        <p className="text-sm sm:text-xs text-gray-500 mb-3">
          {t("tracking_number")} #: {data.tracking_number}
        </p>

        {/* Once settled the outcome replaces the form — nothing is left to
            change and the customer needs to see what happened to the money. */}
        {result ? (
          <div className="flex flex-col gap-3">
            <div className="bg-gray_50 border-2 border-gray_300 rounded-[12px] p-4 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-500">ძველი ფასი</span>
                <span>{Number(result.old_price).toFixed(2)} ₾</span>
              </div>
              <div className="flex justify-between font-semibold">
                <span className="text-gray-500">ახალი ფასი</span>
                <span>{Number(result.new_price).toFixed(2)} ₾</span>
              </div>
            </div>

            {result.outcome === "refunded" && (
              <p className="text-sm text-green-700 bg-green-50 border border-green-200 rounded-[12px] p-3">
                ამანათი გაიაფდა — <b>{Number(result.amount).toFixed(2)} ₾</b>{" "}
                დაგიბრუნდათ ბალანსზე. ბალანსი:{" "}
                <b>{Number(result.balance).toFixed(2)} ₾</b>
              </p>
            )}

            {result.outcome === "charged_balance" && (
              <p className="text-sm text-teal-800 bg-teal-50 border border-teal-200 rounded-[12px] p-3">
                სხვაობა <b>{Number(result.amount).toFixed(2)} ₾</b> ჩამოგეჭრათ
                ბალანსიდან. ბალანსი:{" "}
                <b>{Number(result.balance).toFixed(2)} ₾</b>
              </p>
            )}

            {result.outcome === "needs_payment" && (
              <>
                <p className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-[12px] p-3">
                  <BsExclamationTriangle className="inline mb-0.5" /> ბალანსი არ
                  იყო საკმარისი — დარჩა გადასახდელი{" "}
                  <b>{Number(result.amount).toFixed(2)} ₾</b>. ამანათი გატანამდე
                  უნდა დაიფაროს.
                </p>
                <button
                  type="button"
                  onClick={() => navigate("/personal-cabinet/ballance-top-up")}
                  className="bg-teal_600_b2 border-2 border-solid border-teal_600 rounded-[12px]
                             h-11 text-white text-sm"
                >
                  ბალანსის შევსება
                </button>
              </>
            )}

            {(result.outcome === "price_changed" ||
              result.outcome === "unchanged") && (
              <p className="text-sm text-gray-600 bg-gray_50 border border-gray_300 rounded-[12px] p-3">
                {result.outcome === "unchanged"
                  ? "მისამართი შეიცვალა, ფასი უცვლელია."
                  : `მისამართი შეიცვალა. გადასახდელი: ${Number(
                      result.outstanding
                    ).toFixed(2)} ₾`}
              </p>
            )}

            <button
              type="button"
              onClick={() => setDecShow(false)}
              className="rounded-[12px] bg-gray-200 text-slate-700 text-sm h-11"
            >
              {t("cancel") || "დახურვა"}
            </button>
          </div>
        ) : (
          <>
            <DeliveryAddressPicker value={addressId} onChange={setAddressId} />

            {/* The difference, before anything is charged. */}
            {changed && quote && (
              <div
                className={`mt-3 text-sm rounded-[12px] p-3 border ${
                  delta > 0.005
                    ? "bg-amber-50 border-amber-200 text-amber-800"
                    : delta < -0.005
                    ? "bg-green-50 border-green-200 text-green-800"
                    : "bg-gray_50 border-gray_300 text-gray-600"
                }`}
              >
                <div className="flex justify-between">
                  <span>ამჟამინდელი ფასი</span>
                  <span>{currentPrice.toFixed(2)} ₾</span>
                </div>
                <div className="flex justify-between">
                  <span>ახალი მისამართით</span>
                  <span>{Number(quote.price).toFixed(2)} ₾</span>
                </div>
                <div className="flex justify-between font-bold mt-1 pt-1 border-t border-gray_300">
                  <span>
                    {delta > 0.005
                      ? "დამატებით გადასახდელი"
                      : delta < -0.005
                      ? "დაგიბრუნდებათ"
                      : "სხვაობა"}
                  </span>
                  <span>{Math.abs(delta).toFixed(2)} ₾</span>
                </div>
              </div>
            )}

            <div className="flex gap-2 justify-end mt-5">
              <button
                type="button"
                onClick={() => setDecShow(false)}
                className="rounded-[12px] bg-gray-200 text-slate-700 text-sm px-5 h-11"
              >
                {t("cancel") || "დახურვა"}
              </button>
              <button
                type="button"
                disabled={saving || !addressId}
                onClick={save}
                className="bg-teal_600_b2 border-2 border-solid border-teal_600 rounded-[12px]
                           h-11 px-6 text-white text-sm disabled:opacity-50"
              >
                {saving
                  ? "..."
                  : delta > 0.005
                  ? `დადასტურება (+${delta.toFixed(2)} ₾)`
                  : t("save") || "შენახვა"}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default ChangeDeliveryAddress;
